import React, { useEffect, useRef, useState } from 'react';
import { Alert, Progress } from 'reactstrap';

import { getErrorMessage } from '~libs/axiosWrapper';

import { Spinner } from '~components';
import { InputProps } from '~components/form-fields';
import Icon, { faCircleCheck, faCircleExclamation, faTriangleExclamation } from '~components/icon';

import { columns } from '~support-at-home/forms/reconciliations/errorList';

import { useFileUploadMutation } from '~support-at-home/actions/reconciliations';

import { ErrorDetailsCard, SummaryStatCard } from '../common';

const Process: React.FC<InputProps> = ({ value, onChange }) => {
  const { file } = value?.upload || {};
  const [fileUpload, { data, error, isError }] = useFileUploadMutation();

  const [uploadProgress, setUploadProgress] = useState(0);
  const [apiError, setApiError] = useState<string | null>(null);

  const hasSetReconciliationId = useRef(false);

  useEffect(() => {
    if (!file) return;

    setUploadProgress(0);
    setApiError(null);

    const formData = new FormData();
    formData.append('file', file);

    fileUpload({
      formData,
      onProgress: setUploadProgress,
    });
  }, [file, fileUpload]);

  useEffect(() => {
    if (isError && error) {
      setApiError(getErrorMessage(error));
      setUploadProgress(0);
    }
  }, [isError, error]);

  useEffect(() => {
    if (!data?.reconciliationId) return;

    if (!hasSetReconciliationId.current) {
      onChange?.('reconciliationId', data.reconciliationId);
      hasSetReconciliationId.current = true;
    }
  }, [data, onChange]);

  const { counts } = data || {};
  const { processed = 0, unmatched = 0, skipped = 0, valid = 0, needsReview = 0 } = counts || {};

  const isUploading = !!file && uploadProgress < 100 && !data && !apiError;
  const isProcessing = uploadProgress === 100 && !data && !apiError;
  const isComplete = !!data && !apiError;

  const renderProgressBar = () => {
    if (isUploading) {
      return <Progress value={uploadProgress} style={{ height: '25px' }} color="success" />;
    }

    if (isProcessing) {
      return <Progress value={100} style={{ height: '25px' }} color="success" animated striped />;
    }

    return null;
  };

  return (
    <>
      {/* File error from API - cannot parse file / match claim_item records etc. */}
      {apiError && (
        <Alert color="danger" className="w-100 mb-3">
          <Icon icon={faCircleExclamation} className="me-2" />
          <b>File Processing Error</b>
          <div className="mt-2 mb-0">{apiError}</div>
        </Alert>
      )}

      {/* Upload progress */}
      {!apiError && !isComplete && (
        <>
          <div className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <div className="text-muted">
                {isUploading && 'Uploading…'}
                {isProcessing && 'Processing…'}
              </div>
              <div>{isUploading && `${uploadProgress}%`}</div>
            </div>
            {renderProgressBar()}
          </div>
          <Spinner loading className="spinner-container" />
        </>
      )}

      {isComplete && (
        <>
          {data?.reconciliationId ? (
            <Alert color="success" className="mb-2 p-2 w-100">
              <Icon icon={faCircleCheck} className="me-2" />
              <b>File Processed Successfully</b>
              <div className="mt-2 mb-0">
                Your XML file has been processed and validated. Payment data has been extracted.
              </div>
            </Alert>
          ) : (
            <>
              {data?.message && (
                <Alert color="danger" className="mb-3 w-100">
                  <Icon icon={faTriangleExclamation} className="me-2" />
                  <b>{data.message}</b>
                </Alert>
              )}
              <ErrorDetailsCard
                id="reconciliations"
                title="Error Details"
                errors={data?.errors || []}
                columns={columns}
              />
            </>
          )}
          <div className="d-flex flex-wrap mt-3 gap-4">
            <div className="flex-grow-1">
              <SummaryStatCard count={processed} countLabel="Records Processed" />
            </div>
            <div className="flex-grow-1">
              <SummaryStatCard count={unmatched} countLabel="Unmatched" countClassName="text-danger" />
            </div>
            <div className="flex-grow-1">
              <SummaryStatCard count={skipped} countLabel="Skipped" countClassName="text-primary" />
            </div>
            <div className="flex-grow-1">
              <SummaryStatCard count={valid} countLabel="Valid" countClassName="text-success" />
            </div>
            <div className="flex-grow-1">
              <SummaryStatCard count={needsReview} countLabel="Requires Review" countClassName="text-warning" />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Process;
